import { useQuery } from '@apollo/client';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { useCallback, useContext, useState } from 'react';
import { FETCH_USER_LATEST_INTEGRATION_FORM_AGREEMENT } from './collectSigners.queries';
import { CollectSignersVisual } from './collectSigners.visual';

// TODO: This logic is currently PWL-specific.
// When scaling CollectSigners for other clients, refactor this.
type OnespanRole = {
  roleId: string;
  roleName: string;
  roleType: string;
};

export type SignerInfo = {
  [roleId: string]: {
    firstName: string;
    lastName: string;
    email: string;
  };
};

type CollectSignersProps = {
  options: any;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: any;
};

// TODO: This logic is currently PWL-specific.
// When scaling CollectSigners for other clients, refactor this.
export const isAccountHolder = (role: OnespanRole): boolean =>
  role.roleName === 'client1';

export const CollectSigners = ({
  options,
  userId,
  onNext,
  stepLoading,
  workflowCompletion,
}: CollectSignersProps) => {
  const { sendAnalytic } = useContext(AnalyticsContext);

  const [signingRoles, setSigningRoles] = useState<OnespanRole[]>([]);
  const [externalId, setExternalId] = useState<string>('');
  const [signerInfo, setSignerInfo] = useState<SignerInfo>({});

  // Process the fetched user data to extract integration agreements and signer roles.
  const processUserData = useCallback((data: any): void => {
    const user = data?.fetchUser?.user;
    if (!user) return;

    const formAgreements = user.latestFormAgreements ?? [];
    const integrationAgreement = formAgreements.find(
      (agreement: any) => agreement.type === 'INTEGRATION',
    );

    if (!integrationAgreement) return;

    const integration = integrationAgreement.integrations?.[0];
    if (!integration) return;

    setExternalId(integration.iid);

    // Extract only the signer roles.
    const rawSignerRoles: OnespanRole[] =
      integration.externalData?.signers?.filter(
        (role: any) => role.roleType === 'SIGNER',
      ) ?? [];

    // For account holders, pre-populate signerInfo and merge with role data.
    const enhancedSignerRoles = rawSignerRoles.map((role: OnespanRole) => {
      if (isAccountHolder(role)) {
        setSignerInfo((prev: SignerInfo) => ({
          ...prev,
          [role.roleId]: {
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.primaryEmail,
          },
        }));
        return {
          ...role,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.primaryEmail,
        };
      }
      return role;
    });

    setSigningRoles(enhancedSignerRoles);
  }, []);

  const { loading } = useQuery(FETCH_USER_LATEST_INTEGRATION_FORM_AGREEMENT, {
    variables: { userId },
    fetchPolicy: 'no-cache',
    onCompleted: processUserData,
  });

  // Handle analytics and navigate to the next step.
  const handleNext = useCallback(async () => {
    sendAnalytic(ovAnalyticsEvents.workflowsCollectSignersSendButtonSelect, {
      workflowStepTitle: options?.title,
      workflowId: workflowCompletion?.workflow?.id,
      workflowName: workflowCompletion?.workflow?.name,
      activeWorkflowCompletionId: workflowCompletion?.id,
      objectId: workflowCompletion?.objectId,
      objectType: workflowCompletion?.objectType,
    });
    await onNext();
  }, [sendAnalytic, options, workflowCompletion, onNext]);

  return (
    <CollectSignersVisual
      options={options}
      externalId={externalId}
      signingRoles={signingRoles}
      signerInfo={signerInfo}
      setSignerInfo={setSignerInfo}
      loading={loading || stepLoading}
      onNext={handleNext}
    />
  );
};
